import { runInAction, observable, action } from 'mobx';
import { Logger, createLogger } from '@streem/logger';

export type BrowserPermissionState = PermissionState | 'prompting' | 'error';
// camera and microphone are valid types in typescript v5
type ExtendedPermissionName = PermissionName | 'camera' | 'microphone';

export interface BrowserPermissionConfig {
    constraints: DisplayMediaStreamOptions;
    queryName: ExtendedPermissionName;
}

export class BrowserPermission {
    private initialized: boolean;
    private disposeController = new AbortController();
    private browserPermissionState: BrowserPermissionState | undefined = undefined;
    private abortSignal: AbortSignal;

    @observable
    public hasPermissionGranted: boolean | undefined = undefined;

    protected logger: Logger;

    constructor(private readonly permission: BrowserPermissionConfig) {
        this.logger = createLogger(permission.queryName, {
            name: permission.queryName,
        });

        this.abortSignal = this.disposeController.signal;
    }
    private setInitialized(val: boolean) {
        runInAction(() => {
            this.initialized = val;
        });
    }

    public async initialize(): Promise<boolean> {
        try {
            const status = await navigator.permissions.query({
                name: this.permission.queryName as PermissionName,
            });

            runInAction(() => {
                this.handlePermissionStatus(status.state);
                this.setState(status.state);
            });

            if (!this.abortSignal.aborted) {
                status.addEventListener(
                    'change',
                    () => {
                        runInAction(() => {
                            this.logger.info(
                                `Change in permission for ${this.permission.queryName} detected:`,
                                status.state,
                            );
                            this.setState(status.state);
                            this.handlePermissionStatus(status.state);
                        });
                    },
                    { signal: this.abortSignal },
                );
            }

            this.setInitialized(true);

            return true;
        } catch (error) {
            runInAction(() => {
                this.setState('prompt');
                this.hasPermissionGranted = false;
            });
            this.logger.warn('Error encountered querying permissions status', error);
            return false;
        }
    }

    public dispose(): void {
        this.browserPermissionState = undefined;
        this.disposeController.abort();
    }

    private async getState(): Promise<BrowserPermissionState | undefined> {
        await this.initialized;
        return this.browserPermissionState;
    }

    public async prompt(): Promise<boolean> {
        if (['denied', 'error'].includes(await this.getState())) {
            const isDenied = this.browserPermissionState === 'denied';
            if (isDenied) {
                this.hasPermissionGranted = false;
                this.logger.warn(
                    `Permission was not granted previously for ${this.permission.queryName}, and prompting again is not allowed`,
                );
                return false;
            }
            return !isDenied;
        }

        try {
            runInAction(() => {
                this.setState('prompting');
            });

            const stream = await navigator.mediaDevices.getUserMedia(this.permission.constraints);
            this.disposeOfMediaStream(stream);

            runInAction(() => {
                this.setState('granted');
                this.hasPermissionGranted = true;
            });

            return true;
        } catch (e) {
            runInAction(() => {
                if (
                    typeof e === 'object' &&
                    'name' in e &&
                    (e.name === 'NotAllowedError' || e.name === 'SecurityError')
                ) {
                    this.logger.warn(
                        `Encountered ${e.name} error for ${this.permission.queryName}`,
                    );
                    this.setState('denied');
                } else {
                    this.logger.error(`Call to getUserMedia error`, e);
                    this.setState('error');
                }
                this.hasPermissionGranted = false;
            });

            return false;
        }
    }

    @action
    private setState(state: BrowserPermissionState) {
        if (this.browserPermissionState !== state) {
            this.browserPermissionState = state;
        }
    }

    @action
    private handlePermissionStatus(permissionState: BrowserPermissionState) {
        switch (permissionState) {
            case 'granted':
                this.hasPermissionGranted = true;
                break;
            case 'denied':
                this.hasPermissionGranted = false;
                break;
            case 'prompt':
                this.hasPermissionGranted = false;
                break;
        }
    }

    private disposeOfMediaStream(stream: MediaStream) {
        if (!stream.active) return;
        stream.getTracks().forEach(track => track.stop());
    }
}
